/* eslint-disable camelcase */
import React, { FC, useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { Skeleton, View } from "native-base"
import { ActivityIndicator, Appearance, ImageStyle, TextStyle, TouchableOpacity, useWindowDimensions, ViewStyle } from "react-native"
import {
  AutoImage,
  FlatList,
  LazyRender, ListNetworkError,
  ListRenderItem,
  ListRenderItemInfo,
  Screen,
  SearchBar,
  Text,
} from "../../../components"
import { translate, TxKeyPath } from "../../../i18n"
import { useStores } from "../../../models"
import { navigate } from "../../../navigators"
import { color } from "../../../theme"
import config from "../../../utils/config"
import { SafeAreaView } from "react-native-safe-area-context"
import JalaAnalytic from "../../../utils/jala-analytic"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const LINE: ViewStyle = {
  height: 8,
  backgroundColor: color.palette.gray100
}

const WRAPPER: ViewStyle = {
  marginTop: 22,
  flex: 1
}

const SUB_HEADER: ViewStyle = {
  paddingHorizontal: 16,
}

const CARD_CONTAINER: ViewStyle = {
  flexDirection: 'row',
  borderWidth: 1,
  borderColor: color.palette.gray200,
  borderRadius: 8,
  marginVertical: 8
}

const NEWS_WRAPPER: ViewStyle = {
  flex: 1,
  paddingLeft: 12,
  paddingVertical: 8,
  paddingRight: 8,
}

const IMG: ImageStyle = {
  backgroundColor: color.primary,
  width: 85,
  height: 85,
  borderTopLeftRadius: 8,
  borderBottomLeftRadius: 8,
}

const READ_BTN: ViewStyle = {
  flex: 1,
  width: 40,
  alignItems: 'center',
  alignSelf: 'flex-end',
  justifyContent: 'flex-end'
}

const EMPTY_SEARCH_TEXT: TextStyle = {
  marginTop: 24,
  marginBottom: 8
}

const EMPTY_IMG_WRAPPER: ViewStyle = {
  position: 'relative',
  alignItems: 'center',
  marginTop: 40
}

const EMPTY_IMG: ImageStyle = {
  position: 'absolute',
  width: 150,
  height: 150,
  alignSelf: "center",
  top: 24
}

const CIRCLE: ViewStyle = {
  width: 170,
  height: 170,
  borderRadius: 170 / 2,
  backgroundColor: color.palette.ellipse,
}

const EMPTY_RESULT_TEXT: TextStyle = {
  color: color.palette.blue600,
  marginTop: 30,
  marginBottom: 15,
  textAlign: 'center'
}

const CATEGORY_WRAPPER: ViewStyle = {
  backgroundColor: color.palette.white,
  height: 32,
  borderRadius: 38,
  borderWidth: 1,
  borderColor: color.palette.gray200,
  justifyContent: 'center',
  paddingHorizontal: 16,
  marginHorizontal: 10,
  marginBottom: 17
}

const LIST_CONTAINER: ViewStyle = {
  paddingBottom: 130
}

const CONTENT_CONTAINER: ViewStyle = {
  paddingHorizontal: 16,
  ...LIST_CONTAINER
}


export const RenderItemCategory = ({ item, onPress }: { item: any, onPress?: (data) => void }) => {
  const category = item.short_name || item.name
  const filter = item.short_name ? category : item.slug
  return (
    <TouchableOpacity style={CATEGORY_WRAPPER} onPress={() => onPress(filter)}>
      <Text preset="title4" text={category} color={color.palette.gray600} colorScheme="light" />
    </TouchableOpacity>
  )
}


export const EmptyState: FC<{
  data?: any,
  searchBy?: TxKeyPath,
  colorScheme?: "light" | "dark",
  onPress?: (data) => void,
  error?: any,
  onRefresh?: () => void
}> = (props) => {
  const { data, searchBy, colorScheme = "light", onPress, error, onRefresh } = props;
  const { height } = useWindowDimensions()

  const img = require("./empty-search.png")
  const usedSearch = searchBy || "diseaseScreen.mostSearch";

  if (error)
    return <ListNetworkError
      colorScheme={colorScheme}
      height={`${height - 214}px`}
      onRefresh={onRefresh}
    />

  return (
    // <View style={EMPTY_CONTAINER}>
    <View>
      <View style={EMPTY_IMG_WRAPPER}>
        <View style={CIRCLE} />
        <AutoImage source={img} style={EMPTY_IMG} />
      </View>
      <Text preset="title1" tx="diseaseScreen.empty" style={EMPTY_RESULT_TEXT} colorScheme={colorScheme} />
      <Text preset="title4" tx={usedSearch} mb="12px" textAlign="center" colorScheme={colorScheme} />
      <View flexWrap="wrap" justifyContent="center" flexDirection="row">
        {data.map((item, index) => <RenderItemCategory key={index} item={item} onPress={onPress} />)}
      </View>
      {/* <FlatList
        // contentContainerStyle={LIST}
        data={data}
        showsVerticalScrollIndicator={false}
        keyExtractor={(item, index) => String(index)}
        renderItem={({ item }) => <RenderItemCategory item={item} onPress={onPress} />}
        showsHorizontalScrollIndicator={false}
      /> */}
    </View>
  )
}

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any>, handleNavigate: (item: any) => void }> = observer(function Item(props) {
  const { colorScheme = "light" || Appearance.getColorScheme(), info, handleNavigate } = props;
  const { item, item: { image, full_name } } = info;


  const { BASE_URL } = config();
  const img = image ? { uri: `${BASE_URL}/img/cache/large/${image}`} : require("./empty-photo.png");

  const onNavigate = useCallback(() => handleNavigate(item), [handleNavigate, item])

  const renderPlaceholder = useCallback(() => {
    return <View>
      <Skeleton height="85px" marginBottom="10px" />
    </View>
  }, [])


  return <LazyRender renderPlaceholder={renderPlaceholder}>
    <View style={CARD_CONTAINER}>
      <AutoImage source={img} style={IMG} />
      <View style={NEWS_WRAPPER}>
        <Text preset="title4" numberOfLines={2} ellipsizeMode="tail" text={full_name} colorScheme={colorScheme} />
        <TouchableOpacity style={READ_BTN} onPress={onNavigate}>
          <Text preset="title4" tx="diseaseScreen.read" color={color.palette.blue600} colorScheme={colorScheme} />
        </TouchableOpacity>
      </View>
    </View>
  </LazyRender>;
})

export const DiseaseScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function DiseaseScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  const { diseaseStore, diseaseStore: { error, diseases, categories, loading: loadingModel } } = useStores()

  const [filtered, setFilter] = useState<Array<any>>([]);
  const [isActive, setActive] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");

  // const isEmpty = isActive ? filtered.length === 0 : diseases.length === 0;
  // const isEmpty = diseases.length === 0;
  const isEmpty = isActive ? filtered.length === 0 : diseases.length === 0

  const data = isActive ? filtered : diseases

  useEffect(() => {
    diseaseStore.getDiseases();
  }, [diseaseStore])

  const searchFilter = () => {
    let data = [];
    if (search) {
      if (!isActive) {
        data = diseases.filter((item) => {
          const itemName = item.full_name || item.title;
          const itemData = itemName.toUpperCase();
          const textData = search.toUpperCase();
          return itemData.indexOf(textData) > -1;
        });
        setActive(!!search.length)
      } else {
        setActive(false);
        setSearch("")
      }
    }
    const usedData = search.length ? data : diseases;
    setFilter(usedData);
  }

  const onRefresh = useCallback(() => {
    diseaseStore.set('error', null);
    diseaseStore.getDiseases();
  }, [diseaseStore])

  const handleChangeText = (value: string) => {
    setSearch(value)
    if (value.length === 0) setActive(false)
  };

  // const handleNavigate = (data) => () => {
  const handleNavigate = (data) => {
    JalaAnalytic.log('Mobile app: visit shrimp diseases');
    navigate('Home.Stack', 'Home.Disease.Detail', { id: data.id, data } )
  }

  const handleByCategory = (data) => {
    const filtered = diseases.filter((disease) => {
      const diseaseName = disease.short_name;
      return diseaseName.indexOf(data) > -1;
    });
    setFilter(filtered)
  }

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} handleNavigate={handleNavigate} />
  }, [colorScheme])

  const keyExtractor = useCallback((item) => `${item.id}`, [])

  return (
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === "light" ? "light-content" : "dark-content"}
    >
      <View style={WRAPPER}>
        <View style={SUB_HEADER}>
          <SearchBar
            title={isActive ? "Clear" : "Search"}
            placeholder="diseaseScreen.diseaseSearchPlaceholder"
            onChangeText={handleChangeText}
            onPress={searchFilter}
            value={search}
          />
          {isEmpty && <Text preset="title3" text={translate("newsScreen.search", { search: `${search}` })} style={EMPTY_SEARCH_TEXT} colorScheme={colorScheme} />}
        </View>
        {isEmpty && <View style={LINE} />}
        {/* {isEmpty ? <EmptyState data={categories} onPress={handleByCategory} /> : <List />} */}
        <FlatList
          data={data}
          renderItem={renderItem}
          contentContainerStyle={CONTENT_CONTAINER}
          keyExtractor={keyExtractor}
          ListEmptyComponent={loadingModel && (
            <View flexGrow={1} justifyContent="center" height={`${height - 214}px`}>
              <ActivityIndicator />
            </View>
          ) || <EmptyState data={categories} onPress={handleByCategory} error={error} onRefresh={onRefresh} />}
          ListHeaderComponent={!loadingModel && data.length && (
            <Text preset="title2" tx="diseaseScreen.listDisease" mt="24px" pb="6px" colorScheme={colorScheme} /> || undefined
          )}
          ListFooterComponent={<SafeAreaView edges={['bottom']} />}
          refreshing={false}
          estimatedItemSize={40}
          showsVerticalScrollIndicator={false}
        />
      </View>
    </Screen>
  )
})
